import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useSelector, useDispatch } from 'react-redux'
import debounce from 'lodash/debounce'

import { api } from '@h2/api'
import { features } from '@h2/components/media'

import { VIEW_MODE_BASIC, VIEW_MODE_MANAGEMENT } from '../../constants'
import { user as getUser } from '../../selectors/user'
import { toggleEmailConfirmationDialog } from '../../actions/toggleEmailConfirmationDialog'
import { setUser } from '../../actions/setUser'

/**
 * Hook to provide a global context for the app.
 * CAUTION: Think carefully about useState/useReducer/useSelector/etc.
 * as re-renders here usually mean across the entire tree.
 *
 * @param {object} ctx
 * @returns {object}
 */
const useContextProvider = ctx => {
  const dispatch = useDispatch()
  const navigationRef = useRef()
  const [tenant, system] = ctx.systemName?.split(' ') ?? ['', '']
  const queryClient = useQueryClient()
  const { mutate: mutateViewMode } = useMutation(
    () => api.fetch('/user/toggleview', { method: 'POST' }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userMenu')
      }
    }
  )
  const user = useSelector(getUser)
  const [isMobile, setIsMobile] = useState(
    !window.matchMedia(`(${features.minWidth.laptop})`).matches
  )
  const [viewMode, setViewMode] = useState(
    user?.managementDashboardView ? VIEW_MODE_MANAGEMENT : VIEW_MODE_BASIC
  )
  const isEarlyLearnerMode = useMemo(
    () =>
      viewMode === VIEW_MODE_BASIC &&
      (user?.earlyDashboardActive || ctx.isNavianceElementary),
    [user?.earlyDashboardActive, ctx.isNavianceElementary, viewMode]
  )
  const { mutateAsync: setEarlyDashboard } = useMutation(
    isEnabled => {
      const suffix = isEnabled ? 'enable' : 'disable'

      return api.fetch(`/early/dashboard/${suffix}`, {
        method: 'POST',
        body: {}
      })
    },
    {
      onSuccess: updatedUser => {
        dispatch(setUser(updatedUser))
      }
    }
  )
  const handleOnViewChange = useCallback(async () => {
    if (user?.isNavigator && !user?.hasManager) {
      dispatch(toggleEmailConfirmationDialog('open'))
    } else {
      setViewMode(prevMode => {
        return prevMode === VIEW_MODE_BASIC ? VIEW_MODE_MANAGEMENT : VIEW_MODE_BASIC
      })

      if (ctx.isNavianceElementary) {
        await setEarlyDashboard(viewMode === VIEW_MODE_MANAGEMENT)
      }

      mutateViewMode()
    }
  }, [
    ctx.isNavianceElementary,
    setEarlyDashboard,
    mutateViewMode,
    user,
    viewMode,
    dispatch
  ])
  const handleResize = useMemo(() => {
    return debounce(
      () => {
        setIsMobile(!window.matchMedia(`(${features.minWidth.laptop})`).matches)
      },
      150,
      { leading: true, maxWait: 300 }
    )
  }, [])
  const isNavianceUser = useMemo(
    () => ctx.externalAuthorizationProperties?.source === 'naviance',
    [ctx.externalAuthorizationProperties?.source]
  )
  const backToNavianceURL = useMemo(
    () => (isNavianceUser && '/logout') ?? '',
    [isNavianceUser]
  )
  const context = useMemo(
    () => ({
      user,
      tenant,
      system,
      isMobile,
      viewMode,
      isEarlyLearnerMode,
      isNavianceUser,
      backToNavianceURL,
      setViewMode,
      navigationRef,
      handleOnViewChange,
      setEarlyDashboard,
      isLoggedIn: Boolean(user) || Boolean(ctx.userProfile),
      useReactRouter: Boolean(document.getElementById('spatarget')),
      ...ctx,
      userProfile: user
    }),
    [
      user,
      tenant,
      system,
      isMobile,
      viewMode,
      isEarlyLearnerMode,
      isNavianceUser,
      backToNavianceURL,
      setViewMode,
      handleOnViewChange,
      ctx,
      setEarlyDashboard
    ]
  )

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  return context
}

export { useContextProvider }
